/**
 * ContactUsModal - Displays contact information from the API
 */

import { useState, useEffect } from 'react';
import { OathModal, OathSpinner } from './OathUI';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  UserGroupIcon,
  GlobeAmericasIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { getContactInfo } from '../../services/oathContactInfoService';

const CATEGORY_ICONS = {
  1: PhoneIcon,        // Bankers Surety Services (phones/fax)
  2: UserGroupIcon,    // Customer Service
  3: GlobeAmericasIcon, // Team States
  4: BriefcaseIcon,    // Management
  5: EnvelopeIcon,     // Email
  6: MapPinIcon,       // Addresses
};

// Categories that span both columns
const FULL_WIDTH_CATEGORIES = new Set([1, 3, 6]); // Bankers Surety (few items), Team States, Addresses

// Categories with long values that need stacked (vertical) item layout
const STACKED_ITEM_CATEGORIES = new Set([3, 6]); // Team States, Addresses

// Categories to hide entirely
const HIDDEN_CATEGORIES = new Set([2]); // Customer Service

// For Bankers Surety Services (contactTypeId 1), only show these items
const BANKERS_VISIBLE_ITEMS = new Set(['Toll Free Number', 'Fax Number (main)']);

const ContactUsModal = ({ isOpen, onClose }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError('');

    getContactInfo().then((result) => {
      if (result.success) {
        const EMAIL_TYPE_ID = 5;
        setCategories(result.data.map((cat) =>
          cat.contactTypeId === EMAIL_TYPE_ID
            ? { ...cat, items: [...cat.items].sort((a, b) => a.description.localeCompare(b.description)) }
            : cat
        ));
      } else {
        setError(result.error);
      }
      setLoading(false);
    });
  }, [isOpen]);

  return (
    <OathModal isOpen={isOpen} onClose={onClose} title="Contact Us" size="lg">
      {loading ? (
        <div className="flex justify-center py-12">
          <OathSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-500 dark:text-red-400">
          <p>{error}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {categories.filter((cat) => !HIDDEN_CATEGORIES.has(cat.contactTypeId)).map((cat) => {
            const Icon = CATEGORY_ICONS[cat.contactTypeId] || PhoneIcon;
            const isFullWidth = FULL_WIDTH_CATEGORIES.has(cat.contactTypeId);
            const isStacked = STACKED_ITEM_CATEGORIES.has(cat.contactTypeId);

            return (
              <div
                key={cat.contactTypeId}
                className={`bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 flex flex-col ${
                  isFullWidth ? 'md:col-span-2' : ''
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1 pb-1 border-b border-gray-200 dark:border-gray-600">
                  <div className="p-1 bg-primary/10 dark:bg-primary/20 rounded flex-shrink-0">
                    <Icon className="h-3 w-3 text-primary dark:text-primary-light" />
                  </div>
                  <h3 className="text-xs font-bold text-gray-900 dark:text-white">
                    {cat.contactType}
                  </h3>
                </div>
                <div className={`flex-1 ${
                  isFullWidth
                    ? 'grid grid-cols-1 sm:grid-cols-2 gap-1'
                    : 'space-y-1'
                }`}>
                  {(cat.contactTypeId === 1
                    ? cat.items.filter((item) => BANKERS_VISIBLE_ITEMS.has(item.description))
                    : cat.items
                  ).map((item, idx) => (
                    <div
                      key={idx}
                      className={`px-2.5 py-1.5 rounded-md bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 ${
                        isStacked ? 'flex flex-col gap-1' : 'flex justify-between items-center gap-3'
                      }`}
                    >
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {item.description}
                      </span>
                      <span className={`text-xs font-semibold text-gray-900 dark:text-white ${
                        isStacked ? 'break-words' : 'text-right whitespace-nowrap flex-shrink-0'
                      }`}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </OathModal>
  );
};

export default ContactUsModal;
